import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ujmsCaseProbation, ujmsResolveViolation, ujmsProbationCatalog, ujmsCaseCharges, ujmsOpenOrderPdf, ujmsVopOrderPreview } from "../../services/ujmsApi";
import { CheckinDialog, ViolationDialog } from "../pages/ProbationWorkspace";
import { SentenceForm } from "../pages/SentenceDialog";
import { Panel, Badge, thCls, tdCls, inputCls, selectCls, Field, btnPrimary, btnGhost, fmtD, fmtDT, can, EmptyState } from "../ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { ClipboardList, Gavel, FileText } from "lucide-react";

export const ProbationTab = ({ caseId, access }) => {
  const [data, setData] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [resolving, setResolving] = useState(null);

  const load = useCallback(() => {
    ujmsCaseProbation(caseId).then((r) => setData(r.data)).catch(() => setData({ terms: [], eligible_orders: [] }));
  }, [caseId]);
  useEffect(() => { load(); ujmsProbationCatalog().then((r) => setCatalog(r.data)).catch(() => {}); }, [load]);

  if (!data) return null;
  const manage = can(access, "probation.manage");
  const judge = can(access, "order.sign");

  return (
    <div className="space-y-4" data-testid="tab-probation">
      {data.terms.length === 0 ? (
        <Panel title="Probation supervision" testId="probation-empty-panel">
          <EmptyState icon={ClipboardList} title="No supervision terms on this case"
            note="Probation officers open supervision terms from the Probation Services workspace, tied to a signed sentencing order." testId="probation-empty" />
        </Panel>
      ) : data.terms.map((t) => (
        <Panel key={t.id} testId={`probation-term-${t.id}`}
          title={`${t.supervision_label} — ${t.defendant_name}`}
          action={manage && t.status === "ACTIVE" && (
            <div className="flex gap-1.5">
              <button className={btnGhost} onClick={() => setDialog({ type: "checkin", term: t })} data-testid={`tab-checkin-btn-${t.id}`}>Log Check-in</button>
              <button className={`${btnGhost} border-red-300 text-red-700`} onClick={() => setDialog({ type: "violation", term: t })} data-testid={`tab-violation-btn-${t.id}`}>Report Violation</button>
            </div>
          )}>
          <div className="px-3 py-2 grid grid-cols-2 md:grid-cols-5 gap-x-6 gap-y-1 text-sm border-b border-slate-100">
            <div><div className="text-[10px] uppercase tracking-wider text-slate-400">Status</div><Badge value={t.status} /></div>
            <div><div className="text-[10px] uppercase tracking-wider text-slate-400">Term</div><span className="font-mono text-xs">{t.start_date} → {t.end_date}</span></div>
            <div><div className="text-[10px] uppercase tracking-wider text-slate-400">Officer</div><span className="text-xs">{t.officer_name}</span></div>
            <div><div className="text-[10px] uppercase tracking-wider text-slate-400">Order</div><span className="text-xs">{t.order_title || "—"}</span></div>
            <div><div className="text-[10px] uppercase tracking-wider text-slate-400">Last Contact</div><span className="text-xs">{t.last_checkin ? fmtD(t.last_checkin.occurred_at) : "None"}</span></div>
          </div>
          {(t.conditions || []).length > 0 && (
            <div className="px-3 py-2 border-b border-slate-100">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Conditions</div>
              <div className="flex flex-wrap gap-1">
                {t.conditions.map((c) => <span key={c} className="text-[11px] bg-slate-100 border border-slate-200 rounded-sm px-1.5 py-0.5">{c}</span>)}
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 xl:grid-cols-2">
            <div className="border-r border-slate-100">
              <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-50">Check-ins ({t.checkins.length})</div>
              <table className="w-full border-collapse"><tbody>
                {t.checkins.slice(0, 6).map((c) => (
                  <tr key={c.id}><td className={`${tdCls} font-mono text-xs whitespace-nowrap`}>{fmtDT(c.occurred_at)}</td>
                    <td className={`${tdCls} text-xs`}>{c.checkin_type.replace(/_/g, " ")}</td>
                    <td className={tdCls}><Badge value={c.result} /></td></tr>
                ))}
                {!t.checkins.length && <tr><td className={`${tdCls} text-xs text-slate-400`}>No check-ins logged.</td></tr>}
              </tbody></table>
            </div>
            <div>
              <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-50">Violations ({t.violations.length})</div>
              <table className="w-full border-collapse"><tbody>
                {t.violations.map((v) => (
                  <tr key={v.id} data-testid={`tab-violation-row-${v.id}`}>
                    <td className={tdCls}><span className="text-xs font-semibold">{v.category.replace(/_/g, " ")}</span>
                      <div className="text-xs text-slate-500 line-clamp-2">{v.description}</div>
                      {v.recommendation && <div className="text-[11px] text-slate-500 mt-0.5">PO rec: <span className="italic">{v.recommendation}</span></div>}
                      {v.status === "PENDING_HEARING" && <div className="text-[11px] text-amber-700 mt-0.5" data-testid={`tab-violation-pending-${v.id}`}>Awaiting VOP hearing</div>}
                      {v.resolution && <div className="text-[11px] text-slate-500 mt-0.5">Ruling: <span className="font-semibold">{v.resolution}</span> — {v.resolution_reason}{v.order_id && <button className="ml-1 text-blue-800 hover:underline inline-flex items-center gap-0.5" onClick={() => ujmsOpenOrderPdf(v.order_id, "VOP Order")} data-testid={`vop-order-pdf-${v.id}`}><FileText className="w-3 h-3" /> VOP order</button>}</div>}
                    </td>
                    <td className={tdCls}><Badge value={v.severity} /></td>
                    <td className={tdCls}><Badge value={v.status} /></td>
                    <td className={tdCls}>
                      {judge && (v.status === "REPORTED" || v.status === "PENDING_HEARING") &&
                        <button className={btnGhost} onClick={() => setResolving({ v, term: t })} data-testid={`resolve-btn-${v.id}`}><Gavel className="w-3.5 h-3.5" /> Rule</button>}
                    </td>
                  </tr>
                ))}
                {!t.violations.length && <tr><td className={`${tdCls} text-xs text-slate-400`}>No violations reported.</td></tr>}
              </tbody></table>
            </div>
          </div>
        </Panel>
      ))}

      <CheckinDialog open={dialog?.type === "checkin"} term={dialog?.term} catalog={catalog} onClose={() => setDialog(null)} onDone={() => { setDialog(null); load(); }} />
      <ViolationDialog open={dialog?.type === "violation"} term={dialog?.term} catalog={catalog} onClose={() => setDialog(null)} onDone={() => { setDialog(null); load(); }} />
      <ResolveDialog resolving={resolving} caseId={caseId} catalog={catalog} onClose={() => setResolving(null)} onDone={() => { setResolving(null); load(); }} />
    </div>
  );
};

export function ResolveDialog({ resolving, caseId, catalog, onClose, onDone }) {
  const violation = resolving?.v;
  const term = resolving?.term;
  const [f, setF] = useState({ action: "CONTINUE", reason: "", new_conditions: [] });
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState("form"); // form -> preview -> resentence
  const [orderBody, setOrderBody] = useState("");
  const [resentence, setResentence] = useState(null);
  useEffect(() => { if (violation) { setF({ action: "CONTINUE", reason: "", new_conditions: term?.conditions || [] }); setStage("form"); setOrderBody(""); setResentence(null); } }, [violation]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggleCond = (c) => setF((s) => ({ ...s, new_conditions: s.new_conditions.includes(c) ? s.new_conditions.filter((x) => x !== c) : [...s.new_conditions, c] }));
  const preview = async () => {
    if (!f.reason.trim()) { toast.error("A reason is required"); return; }
    setBusy(true);
    try {
      const r = await ujmsVopOrderPreview(violation.id, { action: f.action, reason: f.reason, new_conditions: f.action === "MODIFY" ? f.new_conditions : null });
      setOrderBody(r.data.body); setStage("preview");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not build the order"); }
    finally { setBusy(false); }
  };
  const signEnter = async () => {
    setBusy(true);
    try {
      const body = { action: f.action, reason: f.reason, order_body: orderBody };
      if (f.action === "MODIFY") body.new_conditions = f.new_conditions;
      await ujmsResolveViolation(violation.id, body);
      toast.success(`VOP order signed & entered: ${f.action}`);
      if (f.action === "REVOKE") {
        let charges = [];
        try { const r = await ujmsCaseCharges(caseId); charges = r.data || []; } catch { /* envelope still loads from the case */ }
        setResentence({ charges }); setStage("resentence"); return;
      }
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!violation} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="resolve-dialog" className="max-w-2xl">
        <DialogHeader><DialogTitle>VOP Determination — {violation?.category?.replace(/_/g, " ")}</DialogTitle></DialogHeader>
        {stage === "resentence" ? (
          <div className="space-y-2" data-testid="resolve-resentence">
            <div className="bg-red-50 border border-red-200 rounded-sm px-3 py-2 text-sm">Probation REVOKED and the order entered. Re-sentence the defendant on the underlying conviction now, or skip.</div>
            <SentenceForm caseId={caseId} charges={resentence.charges} onDone={() => { toast.success("Sentence entered"); onDone(); }} onCancel={onDone} cancelLabel="Skip re-sentencing" testPrefix="vop-resentence" />
          </div>
        ) : stage === "preview" ? (
          <div className="space-y-2" data-testid="resolve-preview">
            <div className="text-[12.5px] text-slate-600">Review and edit the order language. It will be signed by you and entered onto the docket.</div>
            <textarea className={`${inputCls} font-mono text-[11.5px]`} rows={12} value={orderBody} onChange={(e) => setOrderBody(e.target.value)} data-testid="resolve-order-body" />
            <DialogFooter>
              <button className={btnGhost} onClick={() => setStage("form")} data-testid="resolve-preview-back">Back</button>
              <button className={btnPrimary} onClick={signEnter} disabled={busy} data-testid="resolve-sign-btn">Sign &amp; Enter Order</button>
            </DialogFooter>
          </div>
        ) : (
          <>
            <Field label="Determination" required>
              <select className={selectCls} value={f.action} onChange={(e) => setF({ ...f, action: e.target.value })} data-testid="resolve-action-select">
                <option value="STRIKE">STRIKE — dismiss the petition; supervision continues</option>
                <option value="CONTINUE">CONTINUE — supervision continues unchanged</option>
                <option value="MODIFY">MODIFY — change supervision conditions</option>
                <option value="REVOKE">REVOKE — probation revoked (re-sentence)</option>
              </select>
            </Field>
            {f.action === "MODIFY" && (
              <Field label="Conditions now in effect">
                <div className="flex flex-wrap gap-1.5 max-h-40 overflow-auto border border-slate-200 rounded-sm p-2" data-testid="resolve-conditions">
                  {(catalog?.condition_catalog || term?.conditions || []).map((c) => (
                    <label key={c} className="flex items-center gap-1 text-[11px] bg-slate-50 border border-slate-200 rounded-sm px-1.5 py-0.5 cursor-pointer">
                      <input type="checkbox" checked={f.new_conditions.includes(c)} onChange={() => toggleCond(c)} />{c}
                    </label>
                  ))}
                </div>
              </Field>
            )}
            <Field label="Reason / order language" required>
              <textarea className={inputCls} rows={3} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="resolve-reason" />
            </Field>
            <DialogFooter>
              <button className={btnGhost} onClick={onClose}>Cancel</button>
              <button className={btnPrimary} onClick={preview} disabled={busy} data-testid="resolve-submit-btn">Preview Order</button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function _removed_ResolveDialog() { return null; }
